import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { 
  LayoutDashboard, 
  Plus, 
  Archive, 
  Users, 
  Package, 
  FolderOpen,
  Settings,
  Menu,
  X,
  ChevronLeft,
  ChevronRight,
  UserCheck,
  LogOut
} from 'lucide-react';
import './Sidebar.css';

const Sidebar = ({ collapsed, onToggle }) => {
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const location = useLocation();
  const { user, logout } = useAuth();
  const { t } = useTranslation();

  const toggleMobileMenu = () => {
    setMobileOpen(!mobileOpen);
  };

  const navigationItems = [
    {
      path: '/dashboard',
      label: t('sidebar.dashboard'),
      icon: LayoutDashboard,
      color: '#3b82f6'
    },
    {
      path: '/document-creation',
      label: t('sidebar.create_document'),
      icon: Plus,
      color: '#10b981'
    },
    {
      path: '/archives',
      label: t('sidebar.archives'),
      icon: Archive,
      color: '#6366f1'
    },
    {
      path: '/clients',
      label: t('sidebar.clients'),
      icon: Users,
      color: '#f59e0b',
      beta: true
    },
    {
      path: '/employees',
      label: t('sidebar.employees'),
      icon: UserCheck,
      color: '#10b981',
      beta: true
    },
    {
      path: '/products',
      label: t('sidebar.products'),
      icon: Package,
      color: '#ef4444',
      beta: true
    },
    {
      path: '/categories',
      label: t('sidebar.categories'),
      icon: FolderOpen,
      color: '#8b5cf6',
      beta: true
    },

    {
      path: '/settings',
      label: t('sidebar.settings'),
      icon: Settings,
      color: '#64748b'
    }
  ];

  const isActive = (path) => {
    return location.pathname === path;
  };

  return (
    <>
      <button className="mobile-menu-toggle" onClick={toggleMobileMenu}>
        <Menu size={24} />
      </button>
      <aside className={`sidebar ${collapsed ? 'collapsed' : ''} ${mobileOpen ? 'mobile-open' : ''}`}>
        <div className="sidebar-header">
          <div className="brand">
            <div className="brand-icon">
              <img src="/invoit-logo-new.png" alt="INVOIT" className="brand-logo-image" />
            </div>
            {!collapsed && (
              <div className="brand-text">
                <h1 className="brand-title">INVOIT</h1>
                <p className="brand-subtitle">Logiciel de Facturation</p>
              </div>
            )}
          </div>
          <button className="sidebar-toggle" onClick={onToggle} title={collapsed ? t('common.view') : t('common.back')}>
            {collapsed ? <ChevronRight size={20} /> : <ChevronLeft size={20} />}
          </button>
          <button className="mobile-close" onClick={toggleMobileMenu}>
            <X size={20} />
          </button>
        </div>

        <nav className="sidebar-nav">
          <ul className="nav-list">
            {navigationItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);
              
              return (
                <li key={item.path} className="nav-item">
                  <Link
                    to={item.path}
                    className={`nav-link ${active ? 'active' : ''}`}
                  >
                    <div className="nav-icon" style={{ backgroundColor: `${item.color}20`, color: item.color }}>
                      <Icon size={20} />
                    </div>
                    {!collapsed && (
                      <div className="nav-content">
                        <span className="nav-label">{item.label}</span>
                        {item.beta && <span className="pro-badge">PRO</span>}
                        {item.pro && <span className="pro-badge premium">PRO</span>}
                      </div>
                    )}
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>

        <div className="sidebar-footer">
          {!collapsed && (
            <div className="user-info">
              <div className="user-avatar">
                <span>{user?.name?.charAt(0) || 'A'}</span>
              </div>
              <div className="user-details">
                <p className="user-name">{user?.name || t('common.user')}</p>
                <p className="user-role">{user?.role || t('common.user')}</p>
              </div>
              <button className="logout-btn" onClick={logout} title={t('sidebar.logout')}>
                <LogOut size={18} />
              </button>
            </div>
          )}
          {collapsed && (
            <button className="logout-btn collapsed" onClick={logout} title={t('sidebar.logout')}>
              <LogOut size={18} />
            </button>
          )}
        </div>
      </aside>
      {mobileOpen && <div className="sidebar-overlay" onClick={toggleMobileMenu}></div>}
    </>
  );
};

export default Sidebar; 